import { IServerSideDatasource, IServerSideGetRowsRequest } from 'ag-grid-community';
import { IBlotterRequest, ISearchResponse } from 'apiConnector/blotters';
import { BlotterRequest } from 'epic/blotters/tradesBlotter/tradesBlottersEpic';
import { IBlotterItem } from 'models/IBlotterItem';
import { useMemo } from 'react';
import { lastValueFrom, Observable } from 'rxjs';

type ServerSideBlotterDataSourceArg<Request, TopRequest, Item, Error> = {
  blotterRequest: TopRequest;
  translateRequest: (request: IServerSideGetRowsRequest, blotterRequest: TopRequest) => Request;
  searchItems: (req: Request) => Observable<ISearchResponse<Item>>;
  filtersItems?: (items: readonly Item[]) => readonly Item[];
  onItemsReceived?: (response: ISearchResponse<Item>) => void;
  onItemsRequested?: () => void;
  onRequestFailed?: (error: Error) => void;
  isNativePaginationEnabled: boolean;
};

export function useServerSideBlotterDataSource<
  Request extends IBlotterRequest,
  TopRequest = BlotterRequest,
  Item = IBlotterItem,
  Error = undefined,
>({
  blotterRequest,
  translateRequest,
  searchItems,
  filtersItems,
  onItemsReceived,
  onItemsRequested,
  onRequestFailed,
  isNativePaginationEnabled,
}: ServerSideBlotterDataSourceArg<Request, TopRequest, Item, Error>): IServerSideDatasource {
  const serverSideDatasource: IServerSideDatasource = useMemo(() => {
    return {
      getRows: params => {
        const requestData = translateRequest(params.request, blotterRequest);
        onItemsRequested?.();
        lastValueFrom(searchItems(requestData as Request))
          .then(response => {
            if (filtersItems) {
              response.entries = filtersItems(response.entries);
            }
            onItemsReceived?.(response);
            const actualRowCount = response.pageInfo.totalElements;

            if (isNativePaginationEnabled) {
              params.success({
                rowData: response.entries as Item[],
                rowCount: actualRowCount,
              });
            } else {
              params.success({
                rowData: response.entries as Item[],
                rowCount: response.entries.length,
              });
            }
          })
          .catch(err => {
            onRequestFailed?.(err);
            params.fail();
          });
      },
    };
  }, [
    onItemsReceived,
    searchItems,
    onItemsRequested,
    onRequestFailed,
    translateRequest,
    filtersItems,
    blotterRequest,
    isNativePaginationEnabled,
  ]);

  return serverSideDatasource;
}
